import { useEffect, useRef, useState } from 'react';

/**
 * HLS oynatıcı kancası.
 *
 * ## Neden ayrı?
 *
 * Hem tek oynatıcı hem **çoklu ekran** aynı mantığı kullanıyor: bir adres
 * verilir, `hls.js` kurulur, hata durumunda sunucunun mesajı gösterilir.
 * Kod iki yerde tekrarlansaydı ikisi zamanla ayrışırdı.
 */
export interface HlsPlayerState {
  /** `<video>` öğesine bağlanacak ref. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Oynatılamadıysa kullanıcıya gösterilecek mesaj. */
  error: string | null;
  /** Hata mesajını temizler. */
  clearError: () => void;
}

/**
 * Sunucunun hata gövdesinden okunabilir mesajı çıkarır.
 *
 * Relay, sağlayıcı hatasında `{"error":"upstream_failed","message":"…"}` döner.
 * Kullanıcı "Akış oynatılamadı" yerine **gerçek sebebi** görmeli.
 */
export function readUpstreamMessage(data: {
  response?: { code?: number; text?: string };
}): string | null {
  const text = data.response?.text;
  if (typeof text !== 'string' || text.length === 0) return null;
  try {
    const parsed = JSON.parse(text) as { message?: string };
    return typeof parsed.message === 'string' ? parsed.message : null;
  } catch {
    return null;
  }
}

export function useHlsPlayer(url: string | null): HlsPlayerState {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video === null || url === null) return;
    let hls: { destroy: () => void } | null = null;
    let cancelled = false;

    void (async () => {
      const module = await import('hls.js');
      if (cancelled) return;
      const Hls = module.default;
      if (Hls.isSupported()) {
        const instance = new Hls({
          // Canlı akış: üç segment eş zamanlı, süresiz playlist.
          liveSyncDurationCount: 3,
          liveDurationInfinity: true,
          enableWorker: true,
        });
        instance.loadSource(url);
        instance.attachMedia(video);
        instance.on(
          Hls.Events.ERROR,
          (
            _event: unknown,
            data: { fatal?: boolean; response?: { code?: number; text?: string } },
          ) => {
            if (!data.fatal) return;
            setError(readUpstreamMessage(data) ?? 'Akış oynatılamadı.');
          },
        );
        hls = instance;
        return;
      }
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        return;
      }
      setError('Bu tarayıcı HLS oynatmayı desteklemiyor.');
    })();

    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [url]);

  return { videoRef, error, clearError: () => setError(null) };
}
